import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import parsePhoneNumber from 'libphonenumber-js';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import ContactAPI from 'dashboard/api/contacts';
import ConversationApi from 'dashboard/api/conversations';
import { generateLabelForContactableInboxesList } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper.js';

const PHONE_NUMBER_REGEX = /^\+?[\d\s\-().]{7,}$/;

const isPhoneCapableInbox = inbox => {
  const type = inbox.channelType;
  const name = (inbox.name || '').toLowerCase();

  if (name.includes('llamada')) return false;

  return (
    type === INBOX_TYPES.WHATSAPP ||
    type === INBOX_TYPES.WAHA ||
    type === INBOX_TYPES.API
  );
};

const normalizePhone = phone => {
  const digits = phone.replace(/\D/g, '');
  return `+${digits}`;
};

const normalizeToE164 = (phone, defaultCountry) => {
  const parsed = defaultCountry
    ? parsePhoneNumber(phone, defaultCountry)
    : parsePhoneNumber(phone);
  if (parsed?.isValid()) return parsed.format('E.164');
  return normalizePhone(phone);
};

const buildSourceId = (inbox, phone) => {
  const normalized = normalizePhone(phone);
  const digits = normalized.replace(/\D/g, '');

  if (
    inbox.channelType === INBOX_TYPES.WHATSAPP ||
    inbox.channelType === INBOX_TYPES.WAHA
  ) {
    return digits;
  }
  return normalized;
};

export const isPhoneNumber = value => PHONE_NUMBER_REGEX.test(value);

export const useChatFromPhoneNumber = () => {
  const store = useStore();
  const { t } = useI18n();
  const router = useRouter();
  const { defaultPhoneCountry } = useConfig();

  const isLoading = ref(false);

  const inboxesList = computed(
    () => store.getters['inboxes/getAllInboxes'] || []
  );

  const phoneCapableInboxes = computed(() =>
    inboxesList.value.filter(isPhoneCapableInbox)
  );

  const menuItems = computed(() =>
    phoneCapableInboxes.value.map(inbox => ({
      ...inbox,
      action: 'inbox',
      value: inbox.id,
      label: generateLabelForContactableInboxesList({
        name: inbox.name,
        email: inbox.email,
        channelType: inbox.channelType,
        phoneNumber: inbox.phoneNumber,
      }),
      icon: 'i-ri-whatsapp-fill text-[#25D366]',
    }))
  );

  const findContactByPhone = async phone => {
    const queryPayload = {
      payload: [
        {
          attribute_key: 'phone_number',
          filter_operator: 'equal_to',
          values: [normalizeToE164(phone, defaultPhoneCountry)],
          attribute_model: 'standard',
          custom_attribute_type: '',
        },
      ],
    };

    try {
      const {
        data: { payload },
      } = await ContactAPI.filter(1, 'phone_number', queryPayload);
      return payload[0];
    } catch {
      return null;
    }
  };

  const buildContactName = contact => {
    if (contact?.name) return contact.name;
    const firstName = contact?.firstName || contact?.first_name || '';
    const lastName = contact?.lastName || contact?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '';
  };

  const buildAvatarUrl = contact =>
    contact?.avatarUrl ||
    contact?.avatar_url ||
    contact?.thumbnail ||
    contact?.avatar ||
    '';

  const createContactWithInfo = (phone, contact) => {
    const normalizedPhone = normalizeToE164(phone, defaultPhoneCountry);
    const payload = { phone_number: normalizedPhone };
    const name =
      buildContactName(contact) || normalizedPhone.replace(/^\+/, '');
    const avatarUrl = buildAvatarUrl(contact);

    payload.name = name;
    if (avatarUrl) payload.avatar_url = avatarUrl;

    return ContactAPI.create(payload);
  };

  const createOrFindContact = async (phone, contact) => {
    if (contact?.id) return contact;
    const existing = await findContactByPhone(phone);
    if (existing) return existing;

    const {
      data: {
        payload: { contact: newContact },
      },
    } = await createContactWithInfo(phone, contact);

    return newContact;
  };

  const navigateToConversation = conversation => {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: conversation.account_id,
        conversation_id: conversation.id,
      },
    });
  };

  const findExistingConversation = async (contactId, inbox) => {
    try {
      const {
        data: { payload },
      } = await ContactAPI.getConversations(contactId);
      return payload.find(
        conversation => String(conversation.inbox_id) === String(inbox.id)
      );
    } catch {
      return null;
    }
  };

  const startChat = async ({ phone, inbox, contact }) => {
    if (!phone || !inbox) return;

    isLoading.value = true;
    try {
      const targetContact = await createOrFindContact(phone, contact);
      const normalizedPhone = normalizeToE164(phone, defaultPhoneCountry);
      const sourceId = buildSourceId(inbox, normalizedPhone);
      const existingConversation = await findExistingConversation(
        targetContact.id,
        inbox
      );

      if (existingConversation) {
        navigateToConversation(existingConversation);
        return;
      }

      const currentUser = store.getters.getCurrentUser;
      const payload = {
        inbox_id: inbox.id,
        contact_id: Number(targetContact.id),
        assignee_id: currentUser?.id,
      };

      const shouldSendSourceId =
        inbox.channelType === INBOX_TYPES.WHATSAPP ||
        inbox.channelType === INBOX_TYPES.WAHA;

      if (shouldSendSourceId) {
        payload.source_id = sourceId;
      }

      const { data } = await ConversationApi.create(payload);
      const conversation = data.payload || data;

      navigateToConversation(conversation);
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        t('PHONE_CHAT.CREATE_ERROR');
      useAlert(message);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    startChat,
    phoneCapableInboxes,
    menuItems,
    isLoading,
    normalizePhone,
  };
};

export default useChatFromPhoneNumber;
